import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { useVoting } from '../../hooks/useVoting';
import FloatingPanel from '../FloatingPanel/FloatingPanel';
import DynamicFavicon from '../DynamicFavicon/DynamicFavicon';
import Map from '../Map/Map';
import api from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import './VotingScreen.css';
import '../Map/Map.css';

const VotingScreen: React.FC = () => {
  const { t } = useTranslation();
  const williamsburgCenter = { latitude: 40.7081, longitude: -73.9571 };
  const {
    isLoading,
    isInitializing,
    canVote: _canVote, // eslint-disable-line @typescript-eslint/no-unused-vars
    hasVoted,
    canSeeMap,
    isInNYC,
    requiresLocationPermission: _requiresLocationPermission, // eslint-disable-line @typescript-eslint/no-unused-vars
    error,
    location,
    areaHierarchy,
    nextVoteTime,
    reason,
    lastVote,
    checkVotingStatus,
    submitVote,
    clearError
  } = useVoting();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cityStats, setCityStats] = useState<{ totalVotes: number; positiveVotes: number }>({ totalVotes: 0, positiveVotes: 0 });
  const [unlockAnimationActive, setUnlockAnimationActive] = useState(false);
  const [unlockVoteSelection, setUnlockVoteSelection] = useState<'good' | 'bad' | null>(null);
  const pollingTimerRef = useRef<number | null>(null);
  const pollingInFlightRef = useRef(false);
  const pollingFailuresRef = useRef(0);
  const lastInteractionAtRef = useRef(Date.now());

  // Cargar estadísticas de la ciudad para el favicon
  useEffect(() => {
    const loadCityStats = async () => {
      try {
        const allAreasStats = await api.getAllAreasStats();
        
        const boroughs = allAreasStats.filter(area => area.area_type === 'borough');
        
        const totalVotes = boroughs.reduce((sum, b) => sum + b.total_votes, 0);
        const positiveVotes = boroughs.reduce((sum, b) => sum + b.positive_votes, 0);
        
        setCityStats({ totalVotes, positiveVotes });
      } catch (error) {
        console.error('Error loading city stats for favicon:', error);
        // Usar valores por defecto si hay error
        setCityStats({ totalVotes: 0, positiveVotes: 0 });
      }
    };

    loadCityStats();
  }, []);

  // Refresco adaptativo de estado:
  // - 15s activo
  // - 30s inactivo
  // - pausa en background
  // - backoff en errores
  useEffect(() => {
    const ACTIVE_INTERVAL_MS = 15000;
    const IDLE_INTERVAL_MS = 30000;
    const IDLE_THRESHOLD_MS = 60000;

    const clearPollTimer = () => {
      if (pollingTimerRef.current !== null) {
        window.clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };

    const jitterDelay = (baseMs: number) => {
      const jitter = Math.round(baseMs * 0.1 * (Math.random() * 2 - 1));
      return Math.max(1000, baseMs + jitter);
    };

    const backoffMs = () => {
      if (pollingFailuresRef.current <= 0) return 0;
      if (pollingFailuresRef.current === 1) return 10000;
      if (pollingFailuresRef.current === 2) return 20000;
      return 60000;
    };

    const computeBaseInterval = () => {
      if (document.visibilityState !== 'visible') return null;
      const idleForMs = Date.now() - lastInteractionAtRef.current;
      return idleForMs <= IDLE_THRESHOLD_MS ? ACTIVE_INTERVAL_MS : IDLE_INTERVAL_MS;
    };

    const scheduleNext = (baseMs?: number) => {
      clearPollTimer();
      const interval = baseMs ?? computeBaseInterval();
      if (interval === null) return;
      pollingTimerRef.current = window.setTimeout(runPollingCycle, jitterDelay(interval + backoffMs()));
    };

    const runPollingCycle = async (forceImmediate = false) => {
      if (document.visibilityState !== 'visible') {
        clearPollTimer();
        return;
      }
      if (pollingInFlightRef.current) {
        scheduleNext(3000);
        return;
      }

      pollingInFlightRef.current = true;
      try {
        const ok = await checkVotingStatus();
        pollingFailuresRef.current = ok ? 0 : Math.min(pollingFailuresRef.current + 1, 3);
      } finally {
        pollingInFlightRef.current = false;
      }

      if (forceImmediate) {
        scheduleNext(ACTIVE_INTERVAL_MS);
      } else {
        scheduleNext();
      }
    };

    const markInteraction = () => {
      lastInteractionAtRef.current = Date.now();
    };

    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        markInteraction();
        void runPollingCycle(true);
      } else {
        clearPollTimer();
      }
    };

    const interactionEvents: Array<keyof WindowEventMap> = ['pointerdown', 'touchstart', 'keydown', 'scroll', 'focus'];
    interactionEvents.forEach(eventName => {
      window.addEventListener(eventName, markInteraction, { passive: true });
    });
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    window.addEventListener('focus', onVisibilityOrFocus);

    scheduleNext(ACTIVE_INTERVAL_MS);

    return () => {
      clearPollTimer();
      interactionEvents.forEach(eventName => {
        window.removeEventListener(eventName, markInteraction);
      });
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      window.removeEventListener('focus', onVisibilityOrFocus);
    };
  }, [checkVotingStatus]);

  // Refresco liviano de estadísticas para favicon.
  useEffect(() => {
    const reloadCityStats = async () => {
      try {
        const allAreasStats = await api.getAllAreasStats();
        
        const boroughs = allAreasStats.filter(area => area.area_type === 'borough');
        const totalVotes = boroughs.reduce((sum, b) => sum + b.total_votes, 0);
        const positiveVotes = boroughs.reduce((sum, b) => sum + b.positive_votes, 0);
        
        setCityStats({ totalVotes, positiveVotes });
      } catch (error) {
        console.error('Error reloading city stats:', error);
      }
    };

    const interval = setInterval(() => {
      reloadCityStats();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const handleVote = async (isPositive: boolean) => {
    setIsSubmitting(true);
    setUnlockVoteSelection(isPositive ? 'good' : 'bad');
    setUnlockAnimationActive(true);
    try {
      await submitVote(isPositive);
    } finally {
      setIsSubmitting(false);
      window.setTimeout(() => {
        setUnlockAnimationActive(false);
        setUnlockVoteSelection(null);
      }, 850);
    }
  };

  const handleRetry = () => {
    clearError();
    checkVotingStatus();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatNextVoteTime = (timeString: string | null) => {
    if (!timeString) return null;
    
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch {
      return timeString;
    }
  };


  // Pantalla de carga (solo para recargas posteriores)
  if (isLoading) {
    return (
      <Box className="voting-screen">
        <DynamicFavicon totalVotes={cityStats.totalVotes} positiveVotes={cityStats.positiveVotes} />
        <Box className="voting-content">
          <CircularProgress size={60} sx={{ color: '#28A738', mb: 3 }} />
        </Box>
      </Box>
    );
  }

  // Error de geolocalización o ubicación
  if (error && !isInNYC) {
    return (
      <Box className="voting-screen">
        <DynamicFavicon totalVotes={cityStats.totalVotes} positiveVotes={cityStats.positiveVotes} />
        <Box className="voting-content">
          <Alert severity="error" sx={{ mb: 3, maxWidth: 400 }}>
            {error}
          </Alert>
          
          {reason === 'outside_nyc' ? (
            <>
              <Typography variant="h5" className="error-title" gutterBottom>
                Outside New York City
              </Typography>
              <Typography variant="body1" className="error-message" paragraph>
                You must be in New York City to vote. Please visit us when you're in the city!
              </Typography>
              <Button
                variant="contained"
                href="https://hwa.today"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  backgroundColor: '#28A738',
                  '&:hover': { backgroundColor: '#1e7a2a' }
                }}
              >
                Visit hwa.today
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h5" className="error-title" gutterBottom>
                Location Error
              </Typography>
              <Typography variant="body1" className="error-message" paragraph>
                We couldn't determine your location. Please make sure location services are enabled.
              </Typography>
              <Button
                variant="contained"
                onClick={handleRetry}
                sx={{ 
                  backgroundColor: '#28A738',
                  '&:hover': { backgroundColor: '#1e7a2a' }
                }}
              >
                Try Again
              </Button>
            </>
          )}
        </Box>
      </Box>
    );
  }

  // Durante la inicialización, mostrar pantalla de carga
  if (isInitializing) {
    return (
      <Box className="voting-screen">
        <DynamicFavicon totalVotes={cityStats.totalVotes} positiveVotes={cityStats.positiveVotes} />
        <Box className="voting-content">
          <CircularProgress size={60} sx={{ color: '#28A738', mb: 3 }} />
          <Typography variant="h6" className="loading-text">
            {t('status.loading')}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Usuario puede ver el mapa (ha votado en las últimas 24h)
  if (canSeeMap === true && location) {
    if (!isInNYC) {
      return (
        <>
          <DynamicFavicon totalVotes={cityStats.totalVotes} positiveVotes={cityStats.positiveVotes} />
          <Map
            initialLocation={williamsburgCenter}
            initialNeighborhoodId={null}
            isOutsideNYCView={true}
          />
          <FloatingPanel
            onVote={handleVote}
            isSubmitting={false}
            location={williamsburgCenter}
            hasVoted={true}
            canVote={false}
            nextVoteTime={null}
            isInitializing={false}
            lastVote={null}
            isLockedView={false}
            isOutsideNYC={true}
            unlockAnimationActive={false}
            unlockVoteSelection={null}
          />
        </>
      );
    }
    return (
      <>
        <DynamicFavicon totalVotes={cityStats.totalVotes} positiveVotes={cityStats.positiveVotes} />
        <Map
          initialLocation={location}
          initialNeighborhoodId={areaHierarchy?.neighborhood_id || null}
        />
        <FloatingPanel 
          onVote={handleVote}
          isSubmitting={isSubmitting}
          location={location}
          hasVoted={hasVoted}
          canVote={_canVote}
          nextVoteTime={nextVoteTime}
          isInitializing={isInitializing}
          lastVote={lastVote}
          isLockedView={false}
          isOutsideNYC={false}
          unlockAnimationActive={unlockAnimationActive}
          unlockVoteSelection={unlockVoteSelection}
        />
      </>
    );
  }

  // Usuario no puede ver el mapa por estado de voto; el mapa permanece nítido.
  if (canSeeMap === false && isInNYC && location) {
    return (
      <Box className="voting-screen voting-screen-map-mode">
        <DynamicFavicon totalVotes={cityStats.totalVotes} positiveVotes={cityStats.positiveVotes} />
        <Map
          initialLocation={location}
          initialNeighborhoodId={areaHierarchy?.neighborhood_id || null}
        />
        <FloatingPanel
          onVote={handleVote}
          isSubmitting={isSubmitting}
          location={location}
          hasVoted={hasVoted}
          canVote={_canVote}
          nextVoteTime={nextVoteTime}
          isInitializing={isInitializing}
          lastVote={lastVote}
          isLockedView={true}
          isOutsideNYC={false}
          unlockAnimationActive={unlockAnimationActive}
          unlockVoteSelection={unlockVoteSelection}
        />
      </Box>
    );
  }

  // Estado desconocido - mostrar pantalla de carga como fallback
  return (
    <Box className="voting-screen">
      <DynamicFavicon totalVotes={cityStats.totalVotes} positiveVotes={cityStats.positiveVotes} />
      <Box className="voting-content">
        <CircularProgress size={60} sx={{ color: '#28A738', mb: 3 }} />
        <Typography variant="h6" className="loading-text">
          {t('status.loading')}
        </Typography>
      </Box>
    </Box>
  );
};

export default VotingScreen;
